import * as signalR from '@microsoft/signalr';
import { getApiOrigin } from '../config/api';
import { store } from '../store';

/**
 * Conexión SignalR única y compartida al hub /hubs/notifications.
 *
 * Todos los consumidores (OrderReadyListener, KDS, caja, cortes, print status) se
 * suscriben por evento a través de subscribeToHubEvent / useHubEvent; la conexión
 * se abre con el primer suscriptor y se cierra cuando no queda ninguno. Así cada
 * dispositivo mantiene UN websocket, no uno por componente.
 *
 * El tiempo real es una optimización: cada consumidor conserva su polling lento
 * como fallback, así que perder un evento (desconexión, backend caído) solo
 * retrasa la actualización hasta el siguiente poll.
 */

/** Pseudo-evento local: se dispara al recuperar la conexión tras una caída.
 * Los consumidores que refetchean deben suscribirse para ponerse al día de lo
 * que se perdió mientras el socket estuvo caído. No llega del servidor. */
export const RECONNECTED_EVENT = '__reconnected';

// Nombres de eventos del servidor (constantes en NotificationsHub.cs)
export const KITCHEN_ORDERS_CHANGED_EVENT = 'KitchenOrdersChanged';
export const TABLES_CHANGED_EVENT = 'TablesChanged';
export const ORDERS_CHANGED_EVENT = 'OrdersChanged';
export const CASH_MOVEMENTS_CHANGED_EVENT = 'CashMovementsChanged';
export const PENDING_SHIFT_CLOSINGS_CHANGED_EVENT = 'PendingShiftClosingsChanged';
export const PRINT_JOB_CHANGED_EVENT = 'PrintJobChanged';
export const ORDER_READY_EVENT = 'OrderReady';

type Handler = (payload?: any) => void;

const handlersByEvent = new Map<string, Set<Handler>>();

let connection: signalR.HubConnection | null = null;
let boundEvents = new Set<string>();
let refCount = 0;
let startAttempt = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

function dispatchTo(event: string, payload?: unknown) {
  const handlers = handlersByEvent.get(event);
  if (!handlers) return;
  handlers.forEach((handler) => {
    try {
      handler(payload);
    } catch {
      // un handler roto no debe tumbar al resto
    }
  });
}

/** Registra el dispatcher de un evento en la conexión viva (una sola vez por evento). */
function bindEvent(event: string) {
  if (!connection || event === RECONNECTED_EVENT || boundEvents.has(event)) return;
  connection.on(event, (payload) => dispatchTo(event, payload));
  boundEvents.add(event);
}

function buildConnection(): signalR.HubConnection {
  const conn = new signalR.HubConnectionBuilder()
    .withUrl(`${getApiOrigin()}/hubs/notifications`, {
      // Lee el token del store en cada (re)conexión, así sobrevive al refresh de JWT
      accessTokenFactory: () => store.getState().auth.accessToken || '',
    })
    .withAutomaticReconnect({
      // Los dispositivos del restaurante viven conectados todo el turno: reintenta por siempre
      nextRetryDelayInMilliseconds: (retryContext) =>
        Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000),
    })
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  conn.onreconnected(() => dispatchTo(RECONNECTED_EVENT));
  return conn;
}

/**
 * withAutomaticReconnect solo actúa tras una conexión inicial exitosa. Si el
 * backend está caído al montar la página, reintentamos el start con el mismo
 * backoff exponencial mientras siga habiendo suscriptores.
 */
function startLoop() {
  const conn = connection;
  if (!conn) return;
  conn.start().then(
    () => {
      startAttempt = 0;
    },
    () => {
      if (connection !== conn || refCount <= 0) return;
      const delay = Math.min(1000 * Math.pow(2, startAttempt), 30000);
      startAttempt++;
      retryTimer = setTimeout(startLoop, delay);
    }
  );
}

/**
 * Suscribe un handler a un evento del hub. Devuelve la función para desuscribir.
 * Abre la conexión si es el primer suscriptor; la cierra al quedar en cero.
 */
export function subscribeToHubEvent(event: string, handler: Handler): () => void {
  let set = handlersByEvent.get(event);
  if (!set) {
    set = new Set();
    handlersByEvent.set(event, set);
  }
  set.add(handler);
  refCount++;

  if (!connection) {
    connection = buildConnection();
    boundEvents = new Set();
    handlersByEvent.forEach((_handlers, existingEvent) => bindEvent(existingEvent));
    startAttempt = 0;
    startLoop();
  } else {
    bindEvent(event);
  }

  return () => {
    set!.delete(handler);
    if (set!.size === 0) {
      handlersByEvent.delete(event);
    }
    refCount--;
    if (refCount <= 0) {
      refCount = 0;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      const conn = connection;
      connection = null;
      boundEvents = new Set();
      conn?.stop().catch(() => undefined);
    }
  };
}
